import { FC, ReactNode, useRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useClickAway } from 'react-use'
import { SimpleMenuItem } from './SimpleMenuItem'
import { SimpleMenuActivator, SimpleMenuContainer } from './SimpleMenu.styles'
export type MenuItem = {
    id: string
    value: string
    addDivider?: boolean
    leftIcon?: ReactNode
    rightElement?: ReactNode
    isTitle?: boolean
    action?: () => void
    duration?: number
    disabled?: boolean
    selected?: boolean
}

export type MenuProps = {
    items?: MenuItem[]
    open?: boolean
    onClose?: () => void
    onActivate?: () => void
    onSelect?: (item: MenuItem) => void
    children?: TChildren
    activator: TChild
    openLeft?: boolean
    dataTestId?: string
    duration?: number
    isActivatorEnabled?: boolean
    menuPosition?: 'absolute' | 'fixed'
}

const getAnimationVariants = (duration?: number) => ({
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: duration, ease: 'easeOut' },
})

const SimpleMenu: FC<MenuProps> = ({
    items,
    open,
    onClose,
    onActivate,
    children,
    activator,
    openLeft,
    dataTestId,
    duration = 0.2,
    isActivatorEnabled = true,
    menuPosition = 'absolute',
}) => {
    const ref = useRef<HTMLDivElement>(null)
    const activatorRef = useRef<HTMLSpanElement>(null)
    const [position, setPosition] = useState({ top: 0, left: 0 })

    useEffect(() => {
        if (open && activatorRef.current) {
            const rect = activatorRef.current.getBoundingClientRect()

            if (menuPosition === 'fixed') {
                setPosition({
                    top: rect.bottom + 5,
                    left: openLeft ? rect.right - 20 : rect.left,
                })
            } else {
                const scrollTop =
                    window.pageYOffset ?? document.documentElement.scrollTop
                const scrollLeft =
                    window.pageXOffset ?? document.documentElement.scrollLeft

                setPosition({
                    top: rect.bottom + scrollTop + 5,
                    left: openLeft
                        ? rect.right + scrollLeft - 20
                        : rect.left + scrollLeft,
                })
            }
        }
    }, [open, openLeft, menuPosition])

    useClickAway(ref, (event) => {
        const target = event.target as Element
        if (target && target.closest('[data-testid="simple-menu-open"]')) {
            return
        }
        if (open) {
            onClose?.()
        }
    })

    const menuContent = open && (
        <SimpleMenuContainer
            {...getAnimationVariants(duration)}
            data-testid="simple-menu-open"
            openLeft={openLeft}
            top={position.top}
            left={position.left}
            menuPosition={menuPosition}
        >
            {children
                ? children
                : items?.map((item, index) => (
                      <SimpleMenuItem
                          key={item.id}
                          id={item.id}
                          disabled={item?.disabled}
                          action={() => {
                              if (!item?.disabled) {
                                  item?.action?.()
                                  onClose?.()
                              }
                          }}
                          text={item.value}
                          addDivider={item?.addDivider}
                          leftIcon={item?.leftIcon}
                          rightElement={item?.rightElement}
                          isTitle={item?.isTitle}
                          isLastItem={index === (items?.length ?? 0) - 1}
                          selected={item?.selected}
                      />
                  ))}
        </SimpleMenuContainer>
    )

    return (
        <div ref={ref} data-testid={dataTestId ?? 'simple-menu'}>
            <SimpleMenuActivator
                ref={activatorRef}
                data-testid="simple-menu-activator"
                onClick={isActivatorEnabled ? () => onActivate?.() : undefined}
                disabled={!isActivatorEnabled}
            >
                {activator}
            </SimpleMenuActivator>
            {menuContent && createPortal(menuContent, document.body)}
        </div>
    )
}
export default SimpleMenu
